/**
 * Shared pagination query schema for list endpoints (docs/APP_PLAN.md
 * §9.5's "pagination caps on every list endpoint"). One definition so the
 * API and any future client agree on parameter names and the maximum page
 * size a client may request.
 */
import { z } from 'zod';

export const PAGINATION_DEFAULT_LIMIT = 20;
export const PAGINATION_MAX_LIMIT = 50;

/**
 * Builds a pagination schema with a caller-chosen maximum page size, for
 * the few endpoints whose own domain limit is larger than the shared
 * default (today: the completed-sessions list, which backs the rolling
 * 100-game Game History window). The cap still exists and is still
 * enforced by Zod — this only lets one endpoint declare a different one
 * rather than every endpoint inheriting the largest.
 */
export function createPaginationQuerySchema(maxLimit: number = PAGINATION_MAX_LIMIT) {
  return z.object({
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(maxLimit)
      .optional()
      .default(Math.min(PAGINATION_DEFAULT_LIMIT, maxLimit)),
    offset: z.coerce.number().int().min(0).optional().default(0),
  });
}

export const paginationQuerySchema = createPaginationQuerySchema();
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
